import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useSearchParams } from "react-router-dom";

import API from "../../../services/api";
import socket from "../../../services/socket";
import CustomOrderModal from "../../../components/customer/Messaging/CustomOrderModal";
import ConversationList from "../../../components/customer/Messaging/ConversationList";
import ChatHeader from "../../../components/customer/Messaging/ChatHeader";
import Message from "../../../components/customer/Messaging/Message";
import MessageInput from "../../../components/customer/Messaging/MessageInput";
import getImageUrl from "../../../utils/imageUrl";
import "./Messages.css";

function Messages() {

  // =========================================================
  // URL
  // =========================================================

  const [searchParams] =
    useSearchParams();


  // =========================================================
  // STATE
  // =========================================================

  const [
    conversations,
    setConversations,
  ] = useState([]);

  const [
    selectedConversation,
    setSelectedConversation,
  ] = useState(null);

  const [
    messages,
    setMessages,
  ] = useState([]);

  const [
    loadingMessages,
    setLoadingMessages,
  ] = useState(false);

  const [
    messagesError,
    setMessagesError,
  ] = useState("");

  const [
    mobileChatOpen,
    setMobileChatOpen,
  ] = useState(false);


  // =========================================================
  // PROVIDER MENU STATE
  // =========================================================

  const [
    providerMenu,
    setProviderMenu,
  ] = useState([]);

  const [
    menuLoading,
    setMenuLoading,
  ] = useState(false);

  const [
    menuError,
    setMenuError,
  ] = useState("");

  const [
    showProviderMenu,
    setShowProviderMenu,
  ] = useState(false);

  // Selected menu items with quantity
  const [
    selectedMenuItems,
    setSelectedMenuItems,
  ] = useState([]);

const [
  showCustomOrder,
  setShowCustomOrder,
] = useState(false);
  // =========================================================
  // CURRENT USER
  // =========================================================

  const getCurrentUser = () => {

    try {

      const storedUser =
        localStorage.getItem("user");

      if (!storedUser) {
        return null;
      }

      return JSON.parse(storedUser);

    } catch (error) {

      console.error(
        "Customer User Parse Error:",
        error
      );

      return null;
    }
  };


  const currentUser =
    getCurrentUser();


  const currentUserId =
    currentUser?._id ||
    currentUser?.id;


  // =========================================================
  // MESSAGE SCROLL
  // =========================================================

  const messagesEndRef =
    useRef(null);


  const scrollToBottom = () => {

    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });

  };


  // =========================================================
  // LOAD CONVERSATIONS
  // =========================================================

  const loadConversations =
    useCallback(
      async () => {

        try {

          const response =
            await API.get(
              "/conversations/customer"
            );

          if (
            response.data?.success
          ) {

            const loaded =
              response.data.conversations ||
              [];

            setConversations(
              loaded
            );

            return loaded;

          }

          return [];

        } catch (error) {

          console.error(
            "Load Customer Conversations Error:",
            error
          );

          return [];

        }

      },
      []
    );


  // =========================================================
  // LOAD MESSAGES
  // =========================================================

  const loadMessages =
    useCallback(
      async (
        conversationId
      ) => {

        if (!conversationId) {
          return;
        }

        try {

          setLoadingMessages(
            true
          );

          setMessagesError("");

          const response =
            await API.get(
              `/messages/${conversationId}`
            );

          if (
            response.data?.success
          ) {

            setMessages(
              response.data.messages ||
              []
            );

          } else {

            setMessages([]);

          }

        } catch (error) {

          console.error(
            "Load Customer Messages Error:",
            error
          );

          setMessagesError(
            error.response?.data?.message ||
            "Failed to load messages"
          );

          setMessages([]);

        } finally {

          setLoadingMessages(
            false
          );

        }

      },
      []
    );


  // =========================================================
  // MARK CONVERSATION AS READ
  // =========================================================

  const markConversationRead =
    useCallback(
      async (
        conversationId
      ) => {

        if (!conversationId) {
          return;
        }

        try {

          await API.patch(
            `/messages/${conversationId}/read`
          );


          // -----------------------------------------------
          // Update conversation list
          // -----------------------------------------------

          setConversations(
            previous =>
              previous.map(
                conversation => {

                  if (
                    conversation._id !==
                    conversationId
                  ) {

                    return conversation;

                  }

                  return {
                    ...conversation,

                    unreadCustomer: 0,
                  };

                }
              )
          );


          // -----------------------------------------------
          // Update selected conversation
          // -----------------------------------------------

          setSelectedConversation(
            previous => {

              if (
                !previous ||
                previous._id !==
                conversationId
              ) {

                return previous;

              }

              return {
                ...previous,

                unreadCustomer: 0,
              };

            }
          );

        } catch (error) {

          console.error(
            "Mark Customer Conversation Read Error:",
            error
          );

        }

      },
      []
    );


  // =========================================================
  // SELECT CONVERSATION
  // =========================================================

  const handleSelectConversation =
    useCallback(
      async (
        conversation
      ) => {

        if (!conversation) {
          return;
        }

        // Close menu when changing conversation
        setShowProviderMenu(false);

        setSelectedMenuItems([]);

        setProviderMenu([]);

        setMenuError("");

        setSelectedConversation(
          conversation
        );

        setMobileChatOpen(
          true
        );

        await loadMessages(
          conversation._id
        );

        await markConversationRead(
          conversation._id
        );

      },
      [
        loadMessages,
        markConversationRead,
      ]
    );


  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {

    let mounted = true;

    const initializeMessages =
      async () => {

        const loaded =
          await loadConversations();

        if (!mounted) {
          return;
        }


        // -----------------------------------------------
        // Conversation from URL
        // -----------------------------------------------

        const conversationId =
          searchParams.get(
            "conversation"
          );


        if (conversationId) {

          const conversation =
            loaded.find(
              item =>
                item._id ===
                conversationId
            );


          if (conversation) {

            await handleSelectConversation(
              conversation
            );

            return;

          }

        }


        // -----------------------------------------------
        // Automatically select first
        // -----------------------------------------------

        if (
          loaded.length > 0
        ) {

          await handleSelectConversation(
            loaded[0]
          );

        }

      };


    initializeMessages();


    return () => {

      mounted = false;

    };

  }, [
    loadConversations,
    searchParams,
    handleSelectConversation,
  ]);


  // =========================================================
  // SOCKET REGISTRATION
  // =========================================================

  useEffect(() => {

    if (!currentUserId) {
      return;
    }


    const handleConnect =
      () => {

        console.log(
          "🟢 Customer socket connected:",
          socket.id
        );

        socket.emit(
          "register-customer",
          currentUserId
        );

      };


    if (
      socket.connected
    ) {

      handleConnect();

    } else {

      socket.on(
        "connect",
        handleConnect
      );

    }


    return () => {

      socket.off(
        "connect",
        handleConnect
      );

    };

  }, [
    currentUserId,
  ]);


  // =========================================================
  // REAL-TIME NEW MESSAGE
  // =========================================================

  useEffect(() => {

    const handleNewMessage =
      (
        incomingMessage
      ) => {

        console.log(
          "📩 Customer New Message:",
          incomingMessage
        );


        if (
          !incomingMessage
        ) {

          return;

        }


        const conversationId =
          incomingMessage.conversationId;


        // =====================================================
        // CURRENT CONVERSATION
        // =====================================================

        if (
          selectedConversation?._id ===
          conversationId
        ) {

          setMessages(
            previous => {

              const alreadyExists =
                previous.some(
                  message =>
                    message._id ===
                    incomingMessage._id
                );


              if (
                alreadyExists
              ) {

                return previous;

              }


              return [
                ...previous,
                incomingMessage,
              ];

            }
          );


          // Provider sent message
          // while customer is viewing it

          if (
            incomingMessage.senderRole !==
            "customer"
          ) {

            markConversationRead(
              conversationId
            );

          }

        }


        // =====================================================
        // UPDATE CONVERSATION PREVIEW
        // =====================================================

        setConversations(
          previous => {

            return previous.map(
              conversation => {

                if (
                  conversation._id !==
                  conversationId
                ) {

                  return conversation;

                }


                const isCurrent =
                  selectedConversation?._id ===
                  conversationId;


                const isIncomingFromProvider =
                  incomingMessage.senderRole !==
                  "customer";


                return {

                  ...conversation,

                  lastMessage:
                    incomingMessage.message ||
                    conversation.lastMessage,

                  lastMessageAt:
                    incomingMessage.createdAt,

                  lastSenderRole:
                    incomingMessage.senderRole,


                  unreadCustomer:
                    isCurrent
                      ? 0
                      : isIncomingFromProvider
                        ? (
                            conversation.unreadCustomer ||
                            0
                          ) + 1
                        : (
                            conversation.unreadCustomer ||
                            0
                          ),

                };

              }
            );

          }
        );

      };


    socket.on(
      "new-message",
      handleNewMessage
    );


    return () => {

      socket.off(
        "new-message",
        handleNewMessage
      );

    };

  }, [
    selectedConversation,
    markConversationRead,
  ]);


  // =========================================================
  // AUTO SCROLL
  // =========================================================

  useEffect(() => {

    if (
      !loadingMessages
    ) {

      const timer =
        setTimeout(
          scrollToBottom,
          50
        );

      return () =>
        clearTimeout(timer);

    }

  }, [
    messages,
    loadingMessages,
  ]);


  // =========================================================
  // SEND NORMAL MESSAGE
  // =========================================================

const handleSendMessage = async ({
  message,
  messageType = "text",
  orderDetails = null,
}) => {

      if (
        !selectedConversation ||
        !message?.trim()
      ) {

        return;

      }


      try {

        const response =
          await API.post(
            `/messages/${selectedConversation._id}`,
           {
  message: message.trim(),

  messageType,

  orderDetails,
}
          );


        if (
          response.data?.success
        ) {

          const newMessage =
            response.data.data;


          // -----------------------------------------------
          // Add locally
          // -----------------------------------------------

          setMessages(
            previous => {

              const alreadyExists =
                previous.some(
                  item =>
                    item._id ===
                    newMessage._id
                );


              if (
                alreadyExists
              ) {

                return previous;

              }


              return [
                ...previous,
                newMessage,
              ];

            }
          );


          // -----------------------------------------------
          // Update conversation
          // -----------------------------------------------

          setConversations(
            previous =>
              previous.map(
                conversation => {

                  if (
                    conversation._id !==
                    selectedConversation._id
                  ) {

                    return conversation;

                  }


                  return {

                    ...conversation,

                    lastMessage:
                      newMessage.message,

                    lastMessageAt:
                      newMessage.createdAt,

                    lastSenderRole:
                      "customer",

                    unreadCustomer: 0,

                  };

                }
              )
          );


          // -----------------------------------------------
          // Update selected conversation
          // -----------------------------------------------

          setSelectedConversation(
            previous => {

              if (!previous) {
                return previous;
              }


              return {

                ...previous,

                lastMessage:
                  newMessage.message,

                lastMessageAt:
                  newMessage.createdAt,

                lastSenderRole:
                  "customer",

                unreadCustomer: 0,

              };

            }
          );

        }

      } catch (error) {

        console.error(
          "Customer Send Message Error:",
          error
        );

        throw error;

      }

    };


  // =========================================================
  // BACK
  // =========================================================

  const handleBack = () => {

    setMobileChatOpen(
      false
    );

  };


  // =========================================================
  // CALL PROVIDER
  // =========================================================

  const handleCall = (
    phoneNumber
  ) => {

    if (!phoneNumber) {
      return;
    }

    window.location.href =
      `tel:${phoneNumber}`;

  };


  // =========================================================
  // EMPTY CHAT
  // =========================================================

  const renderEmptyChat =
    () => {

      return (

        <div className="messages-empty-chat">

          <div className="messages-empty-icon">
            💬
          </div>

          <h2>
            Your HomeFoods Messages
          </h2>

          <p>
            Select a provider from the left
            to start chatting and discuss
            your food, cake, catering or
            provision requirements.
          </p>

        </div>

      );

    };


  // =========================================================
  // OPEN PROVIDER MENU
  // =========================================================

 
const handleOpenProviderMenu =
  async () => {

    if (!selectedConversation) {

      console.error(
        "❌ No selected conversation"
      );

      return;

    }


    console.log(
      "🟢 Opening provider menu"
    );

    console.log(
      "Selected Conversation:",
      selectedConversation
    );


    // =======================================================
    // GET PROVIDER
    // =======================================================

    const provider =
      selectedConversation.providerId ||
      selectedConversation.provider ||
      null;


    console.log(
      "Provider:",
      provider
    );


    // =======================================================
    // GET PROVIDER ID
    // =======================================================

    let providerId = null;


    if (
      typeof provider ===
      "object"
    ) {

      providerId =
        provider?._id ||
        provider?.id ||
        null;

    } else {

      providerId =
        provider ||
        null;

    }


    console.log(
      "🍽️ Provider ID:",
      providerId
    );


    // =======================================================
    // VALIDATE PROVIDER
    // =======================================================

    if (!providerId) {

      console.error(
        "❌ Provider ID not found"
      );

      setProviderMenu([]);

      setMenuError(
        "Provider information is unavailable"
      );

      setShowProviderMenu(
        true
      );

      return;

    }


    // =======================================================
    // RESET OLD SELECTION
    // =======================================================

    setSelectedMenuItems([]);

    setProviderMenu([]);

    setShowProviderMenu(
      true
    );

    setMenuLoading(
      true
    );

    setMenuError("");


    // =======================================================
    // LOAD CHAT MENU
    // =======================================================

    try {

      console.log(
        `📡 Loading Chat Menu for provider: ${providerId}`
      );


      const response =
        await API.get(
          `/chat-menu/provider/${providerId}`
        );


      console.log(
        "✅ Chat Menu response:",
        response.data
      );


      if (
        response.data?.success
      ) {

        const menuItems =
          response.data.menuItems ||
          [];


        console.log(
          "🍽️ Available Chat Menu Items:",
          menuItems
        );


        setProviderMenu(
          menuItems
        );

      } else {

        setProviderMenu([]);

        setMenuError(
          response.data?.message ||
          "Unable to load provider menu"
        );

      }

    } catch (error) {

      console.error(
        "❌ Load Provider Chat Menu Error:",
        error
      );


      console.error(
        "Backend response:",
        error.response?.data
      );


      console.error(
        "Status:",
        error.response?.status
      );


      setProviderMenu([]);

      setMenuError(
        error.response?.data?.message ||
        "Failed to load provider menu"
      );

    } finally {

      setMenuLoading(
        false
      );

    }

  };

// =========================================================
// OPEN CUSTOM ORDER
// =========================================================

// =========================================================
// OPEN CUSTOMER CUSTOM ORDER
// =========================================================

const handleOpenCustomOrder = () => {

  if (!selectedConversation) {

    console.error(
      "❌ No selected conversation"
    );

    return;

  }

  if (
    selectedConversation.status !==
    "active"
  ) {

    return;

  }

  // Close quick actions
  setShowCustomOrder(true);

};
  // =========================================================
  // CLOSE PROVIDER MENU
  // =========================================================

  const handleCloseProviderMenu =
    () => {

      setShowProviderMenu(
        false
      );

      setMenuError("");

      setSelectedMenuItems([]);

    };


  // =========================================================
  // ADD MENU ITEM / INCREASE QUANTITY
  // =========================================================

  const handleAddMenuItem =
    (food) => {

      if (!food) {
        return;
      }


      setSelectedMenuItems(
        previous => {

          const existing =
            previous.find(
              item =>
                item._id ===
                food._id
            );


          // -----------------------------------------------
          // Already selected
          // Increase quantity
          // -----------------------------------------------

          if (existing) {

            return previous.map(
              item => {

                if (
                  item._id !==
                  food._id
                ) {

                  return item;

                }


                return {

                  ...item,

                  quantity:
                    item.quantity + 1,

                };

              }
            );

          }


          // -----------------------------------------------
          // First selection
          // -----------------------------------------------

          return [

            ...previous,

            {

              ...food,

              quantity: 1,

            },

          ];

        }
      );

    };


  // =========================================================
  // DECREASE MENU ITEM QUANTITY
  // =========================================================

  const handleDecreaseMenuItem =
    (foodId) => {

      setSelectedMenuItems(
        previous => {

          return previous

            .map(
              item => {

                if (
                  item._id !==
                  foodId
                ) {

                  return item;

                }


                return {

                  ...item,

                  quantity:
                    item.quantity - 1,

                };

              }
            )

            .filter(
              item =>
                item.quantity > 0
            );

        }
      );

    };


  // =========================================================
  // SEND MENU REQUEST
  // =========================================================
// =========================================================
// SEND MENU ORDER REQUEST
// =========================================================

const handleSendMenuRequest = async () => {

  if (
    !selectedConversation ||
    selectedMenuItems.length === 0
  ) {
    return;
  }

  try {

    // ==========================================
    // Prepare order items
    // ==========================================

    const orderItems =
      selectedMenuItems.map(item => {

       const finalPrice =
  Number(item.price);

        return {

          foodId:
            item._id,

          foodName:
            item.foodName,

          quantity:
            Number(item.quantity) || 1,

          price:
            finalPrice,

          unit:
            item.unit || "item",

        };

      });


    // ==========================================
    // Calculate total
    // ==========================================

    const totalAmount =
      orderItems.reduce(
        (total, item) =>
          total +
          (
            Number(item.price) || 0
          ) *
          (
            Number(item.quantity) || 1
          ),
        0
      );


    // ==========================================
    // Display message
    // ==========================================

    const requestLines =
      orderItems.map(item =>
        `${item.foodName} × ${item.quantity} ₹${item.price} / ${item.unit}`
      );


    const requestMessage =
      `🍽️ Order Request\n\n` +
      requestLines.join("\n") +
      `\n\nPlease confirm availability.`;


    // ==========================================
    // Send to backend
    // ==========================================

    await API.post(
      `/messages/${selectedConversation._id}`,
      {

        message:
          requestMessage,

        messageType:
          "order_request",

        orderDetails: {

          status:
            "pending",

          items:
            orderItems,

          totalAmount:
            totalAmount,

        },

      }
    );


    console.log(
      "✅ Order request sent:",
      orderItems
    );


    // ==========================================
    // Clear selected menu
    // ==========================================

    setSelectedMenuItems([]);


    // ==========================================
    // Close menu
    // ==========================================

    setShowProviderMenu(false);


    // ==========================================
    // Reload messages
    // ==========================================

    await loadMessages(
      selectedConversation._id
    );

  } catch (error) {

    console.error(
      "❌ Send Menu Order Request Error:",
      error
    );

  }

};

  // =========================================================
  // RENDER CHAT
  // =========================================================

  const renderChat =
    () => {

      if (
        !selectedConversation
      ) {

        return renderEmptyChat();

      }


      return (

        <div className="messages-chat">


          {/* =================================================
              CHAT HEADER
          ================================================= */}

         <ChatHeader
  mode="customer"
  conversation={selectedConversation}
  onBack={handleBack}
  onCall={handleCall}
/>


          {/* =================================================
              MESSAGE LIST
          ================================================= */}

          <div className="messages-list">


            {/* ===============================================
                LOADING
            =============================================== */}

            {loadingMessages && (

              <div className="messages-loading">

                Loading messages...

              </div>

            )}


            {/* ===============================================
                ERROR
            =============================================== */}

            {!loadingMessages &&
              messagesError && (

                <div className="messages-error">

                  <p>
                    {messagesError}
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      loadMessages(
                        selectedConversation._id
                      )
                    }
                  >

                    Retry

                  </button>

                </div>

            )}


            {/* ===============================================
                EMPTY CHAT
            =============================================== */}

            {!loadingMessages &&
              !messagesError &&
              messages.length === 0 && (

                <div className="messages-first-chat">

                  <div className="messages-first-chat-icon">
                    💬
                  </div>

                  <h3>
                    Start the conversation
                  </h3>

                  <p>
                    Talk directly with this
                    provider about your order.
                  </p>

                </div>

            )}


            {/* ===============================================
                MESSAGES
            =============================================== */}

            {!loadingMessages &&
              !messagesError &&
              messages.map(
                message => (

                  <Message
                    key={
                      message._id
                    }
                    message={
                      message
                    }
                    currentUserId={
                      currentUserId
                    }
                  />

                )
              )}


            {/* ===============================================
                SCROLL END
            =============================================== */}

            <div
              ref={
                messagesEndRef
              }
            />

          </div>


          {/* =================================================
              PROVIDER MENU
          ================================================= */}

          {showProviderMenu && (

            <div className="provider-chat-menu">


              {/* =================================================
                  MENU HEADER
              ================================================= */}

              <div className="provider-chat-menu-header">

                <div>

                  <span>
                    HomeFoods Menu
                  </span>

                  <h3>
                    Select what you want
                  </h3>

                </div>


                <button
                  type="button"
                  onClick={
                    handleCloseProviderMenu
                  }
                  aria-label="Close menu"
                >

                  ×

                </button>

              </div>


              {/* =================================================
                  MENU LOADING
              ================================================= */}

              {menuLoading && (

                <div className="provider-menu-loading">

                  Loading menu...

                </div>

              )}


              {/* =================================================
                  MENU ERROR
              ================================================= */}

              {!menuLoading &&
                menuError && (

                  <div className="provider-menu-error">

                    {menuError}

                  </div>

              )}


              {/* =================================================
                  EMPTY MENU
              ================================================= */}

              {!menuLoading &&
                !menuError &&
                providerMenu.length === 0 && (

                  <div className="provider-menu-empty">

                    <div>
                      🍽️
                    </div>

                    <p>
                      This provider has no available
                      menu items right now.
                    </p>

                  </div>

              )}


              {/* =================================================
                  MENU ITEMS
              ================================================= */}

              {!menuLoading &&
                !menuError &&
                providerMenu.length > 0 && (

                  <div className="provider-menu-items">


                    {providerMenu.map(
                      food => {

                        // ---------------------------------------
                        // Final price
                        // ---------------------------------------

                        const finalPrice =
                          Number(
                            food.offerPrice
                          ) > 0

                            ? Number(
                                food.offerPrice
                              )

                            : Number(
                                food.price
                              );


                        // ---------------------------------------
                        // Find selected item
                        // ---------------------------------------

                        const selectedItem =
                          selectedMenuItems.find(
                            item =>
                              item._id ===
                              food._id
                          );


                        // ---------------------------------------
                        // Current quantity
                        // ---------------------------------------

                        const quantity =
                          selectedItem?.quantity ||
                          0;


                        return (

                          <div
                            key={
                              food._id
                            }
                            className="provider-menu-item"
                          >


                            {/* =================================
                                FOOD IMAGE
                            ================================= */}

                            <div className="provider-menu-image">

                            {food.image ? (

  <img
    src={getImageUrl(food.image)}
    alt={food.foodName || "Food"}
    onError={(event) => {
      event.currentTarget.style.display = "none";
    }}
  />

) : (

  <span>
    🍽️
  </span>

)}

                             

                            </div>


                            {/* =================================
                                FOOD INFORMATION
                            ================================= */}

                            <div className="provider-menu-info">

                              <div className="provider-menu-name">

                                {
                                  food.foodName
                                }

                              </div>


                              <div className="provider-menu-description">

                                {
                                  food.description ||
                                  food.foodName
                                }

                              </div>


                              <div className="provider-menu-price">

                                ₹
                                {
                                  finalPrice
                                }

                                <span>

                                  /
                                  {
                                    food.unit ||
                                    "item"
                                  }

                                </span>

                              </div>

                            </div>


                            {/* =================================
                                QUANTITY CONTROLS
                            ================================= */}

                            {quantity === 0 ? (

                              // ---------------------------------
                              // FIRST +
                              // ---------------------------------

                              <button
                                type="button"
                                className="provider-menu-select"
                                onClick={() =>
                                  handleAddMenuItem(
                                    food
                                  )
                                }
                                disabled={
                                  selectedConversation?.status !==
                                  "active"
                                }
                                aria-label={
                                  `Add ${food.foodName}`
                                }
                              >

                                +

                              </button>

                            ) : (

                              // ---------------------------------
                              // − QUANTITY +
                              // ---------------------------------

                              <div
                                className="provider-menu-quantity"
                              >

                                {/* MINUS */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDecreaseMenuItem(
                                      food._id
                                    )
                                  }
                                  disabled={
                                    selectedConversation?.status !==
                                    "active"
                                  }
                                  aria-label={
                                    `Remove one ${food.foodName}`
                                  }
                                >

                                  −

                                </button>


                                {/* QUANTITY */}

                                <span>

                                  {
                                    quantity
                                  }

                                </span>


                                {/* PLUS */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    handleAddMenuItem(
                                      food
                                    )
                                  }
                                  disabled={
                                    selectedConversation?.status !==
                                    "active"
                                  }
                                  aria-label={
                                    `Add one more ${food.foodName}`
                                  }
                                >

                                  +

                                </button>

                              </div>

                            )}

                          </div>

                        );

                      }
                    )}

                  </div>

              )}


              {/* =================================================
                  SEND REQUEST FOOTER
              ================================================= */}

              {!menuLoading &&
                !menuError &&
                selectedMenuItems.length > 0 && (

                  <div className="provider-menu-footer">


                    {/* =========================================
                        SELECTED COUNT
                    ========================================= */}

                    <div className="provider-menu-selected-count">

                      {
                        selectedMenuItems.reduce(
                          (
                            total,
                            item
                          ) =>
                            total +
                            item.quantity,
                          0
                        )
                      }

                      {" "}
                      item(s) selected

                    </div>


                    {/* =========================================
                        SEND REQUEST
                    ========================================= */}

                    <button
                      type="button"
                      className="provider-menu-send"
                      onClick={
                        handleSendMenuRequest
                      }
                      disabled={
                        selectedConversation?.status !==
                        "active"
                      }
                    >

                      <span>
                        Send Request
                      </span>

                      <span>
                        ➤
                      </span>

                    </button>

                  </div>

              )}

            </div>

          )}

{showCustomOrder && (

  <CustomOrderModal

    provider={
      selectedConversation?.providerId ||
      selectedConversation?.provider ||
      null
    }

    providerType={
      (
        selectedConversation?.providerId ||
        selectedConversation?.provider
      )?.providerType ||
      "Food Provider"
    }

    onClose={() =>
      setShowCustomOrder(false)
    }

   onSubmit={
  async (orderDetails) => {

    try {

      console.log(
        "🛒 CUSTOM ORDER REQUEST:",
        orderDetails
      );


      // =====================================================
      // GET PROVIDER
      // =====================================================

      const provider =
        selectedConversation?.providerId ||
        selectedConversation?.provider ||
        null;


      const providerId =
        typeof provider === "object"
          ? provider?._id ||
            provider?.id
          : provider;


      if (!providerId) {

        throw new Error(
          "Provider information is unavailable."
        );

      }


      // =====================================================
      // DETERMINE PROVIDER TYPE
      // =====================================================

      const currentProviderType =
        orderDetails.providerType ||
        provider?.providerType ||
        selectedConversation?.providerType ||
        "Food Provider";


      const isCatering =
        currentProviderType ===
        "Catering";


      console.log(
        "🏷️ Provider Type:",
        currentProviderType
      );

      console.log(
        "🍽️ Is Catering:",
        isCatering
      );


      // =====================================================
      // BUILD BACKEND REQUEST
      // =====================================================

      let requestBody = {

        providerId,

        providerType:
          currentProviderType,

      };


      // =====================================================
      // CATERING ORDER
      // =====================================================

      if (isCatering) {

        requestBody = {

          ...requestBody,

          orderType:
            "custom",

          functionName:
            orderDetails.functionName,

          functionDate:
            orderDetails.functionDate,

          guests:
            Number(
              orderDetails.guests
            ),

          menuItems:
            orderDetails.menuItems,

          description:
            orderDetails.description ||
            "",

        };

      }


      // =====================================================
      // NORMAL CUSTOM ORDER
      // Bakery / Provision / Home Chef
      // =====================================================

      else {

        requestBody = {

          ...requestBody,

          orderType:
            "custom",

          items:
            orderDetails.items,

          specialInstructions:
            orderDetails.specialInstructions ||
            "",

        };

      }


      console.log(
        "📤 Sending Custom Order:",
        requestBody
      );


      // =====================================================
      // CREATE CUSTOM ORDER IN DATABASE
      // =====================================================

      const response =
        await API.post(
          "/custom-orders",
          requestBody
        );


      console.log(
        "✅ Custom Order Backend Response:",
        response.data
      );


      if (
        !response.data?.success
      ) {

        throw new Error(
          response.data?.message ||
          "Failed to create custom order."
        );

      }


      const createdOrder =
        response.data.order;


      console.log(
        "✅ Custom Order Created:",
        createdOrder
      );


      // =====================================================
      // CREATE CHAT MESSAGE
      // =====================================================

      let requestMessage = "";


      let chatOrderDetails = {};


      // =====================================================
      // CATERING CHAT MESSAGE
      // =====================================================

      if (isCatering) {

        const menuLines =
          (
            orderDetails.menuItems ||
            []
          ).map(
            (item, index) =>
              `${index + 1}. ${item.itemName}`
          );


        requestMessage =
          `🍽️ Catering Order Request\n\n` +

          `Function: ${orderDetails.functionName}\n` +

          `Date: ${orderDetails.functionDate}\n` +

          `Guests: ${orderDetails.guests}\n\n` +

          `Menu Items:\n` +

          menuLines.join("\n") +

          `\n\n` +

          `Description:\n` +

          (
            orderDetails.description?.trim() ||
            "No additional description."
          ) +

          `\n\n` +

          `Please review the catering requirements and add your price.`;


        chatOrderDetails = {

          orderId:
            createdOrder._id,

          status:
            createdOrder.customOrderStatus ||
            "Pending Provider Review",

          functionName:
            orderDetails.functionName,

          functionDate:
            orderDetails.functionDate,

          guests:
            Number(
              orderDetails.guests
            ),

          menuItems:
            orderDetails.menuItems,

          description:
            orderDetails.description ||
            "",

        };

      }


      // =====================================================
      // NORMAL CUSTOM ORDER CHAT MESSAGE
      // =====================================================

      else {

        const requestLines =
          (
            orderDetails.items ||
            []
          ).map(
            (item) =>
              `${item.itemName} × ${item.quantity} ${item.unit}`
          );


        requestMessage =
          `🛒 Custom Order Request\n\n` +

          requestLines.join("\n") +

          `\n\n` +

          `The customer has sent a custom order request. ` +

          `Please review the items and add your prices.`;


        chatOrderDetails = {

          orderId:
            createdOrder._id,

          status:
            createdOrder.customOrderStatus ||
            "Pending Provider Review",

          items:
            orderDetails.items,

          specialInstructions:
            orderDetails.specialInstructions ||
            "",

        };

      }


      // =====================================================
      // SEND CHAT MESSAGE
      // =====================================================

      const messageResponse =
        await API.post(
          `/messages/${selectedConversation._id}`,
          {

            message:
              requestMessage,

            messageType:
              "custom_order_request",

            orderDetails:
              chatOrderDetails,

          }
        );


      console.log(
        "✅ Custom order chat message sent:",
        messageResponse.data
      );


      // =====================================================
      // CLOSE MODAL
      // =====================================================

      setShowCustomOrder(
        false
      );


      // =====================================================
      // RELOAD CHAT
      // =====================================================

      await loadMessages(
        selectedConversation._id
      );


      // =====================================================
      // SUCCESS
      // =====================================================

      console.log(
        "🎉 Custom order request successfully sent to provider."
      );


    } catch (error) {

      console.error(
        "❌ Custom Order Submit Error:",
        error
      );


      console.error(
        "Backend Response:",
        error.response?.data
      );


      throw error;

    }

  }
}

  />

)}
          {/* =================================================
              MESSAGE INPUT
          ================================================= */}
<MessageInput
  mode="customer"

  conversation={
    selectedConversation
  }

  onSendMessage={
    handleSendMessage
  }

  onOpenMenu={
    handleOpenProviderMenu
  }

  onOpenOrder={
    handleOpenCustomOrder
  }

  disabled={
    selectedConversation.status !==
    "active"
  }
/>

        </div>

      );

    };


  // =========================================================
  // MAIN UI
  // =========================================================

  return (

    <div
      className={`messages-page ${
        mobileChatOpen
          ? "mobile-chat-open"
          : ""
      }`}
    >

      <div className="messages-container">


        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <div className="messages-header">

          <div>

           <div className="messages-eyebrow">
 <img
  src="/images/logo.png"
  alt="HomeFoods"
/>
</div>

            
         <p>
  Your food, your way — chat, customize and book your order.
</p>

          </div>

        </div>


        {/* =================================================
            MESSAGING AREA
        ================================================= */}

        <div className="messages-content">


          {/* =================================================
              CONVERSATION LIST
          ================================================= */}

          <div className="messages-conversation-panel">

            <ConversationList
              mode="customer"
              selectedConversation={
                selectedConversation
              }
              onSelectConversation={
                handleSelectConversation
              }
              conversations={
                conversations
              }
            />

          </div>


          {/* =================================================
              CHAT
          ================================================= */}

          <div className="messages-chat-wrapper">

            {renderChat()}

          </div>

        </div>

      </div>

    </div>

  );

}

export default Messages;